import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, onSnapshot } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Truck, User, MapPin, Clock, Paperclip, FileText, Printer, CheckCircle, FileCheck, ChevronDown, ChevronUp, Package, ClipboardCheck, ShieldCheck } from 'lucide-react';
import OrderPreview from './OrderPreview.js';
import MOCPreviewContent from './MOCPreviewContent.js';
import QCPreview from './QCPreview.js';
import DCPreviewContent from './DCPreviewContent.js';
import HorizontalTimeline from './components/HorizontalTimeline.js';
import AttachmentCard from './components/AttachmentCard.js';
import StatusBadge from './components/StatusBadge.js';
import { getSalesShippingSnapshot, getShippingSnapshotAddress } from './utils/shippingSnapshot.js';

const formatAddress = (addr) => {
  if (!addr) return '';
  return `${addr.address || ""}, ${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`;
};

const getSalesManagerApprovalTime = (orderData = {}) => {
  if (Array.isArray(orderData.salesReviewHistory)) {
    const approvalReview = [...orderData.salesReviewHistory]
      .reverse()
      .find((review) => review?.status === 'in_que' && review?.reviewedAt);

    if (approvalReview?.reviewedAt) return approvalReview.reviewedAt;
  }

  return orderData.salesReviewedAt || '';
};

const getSalesManagerApprovalName = (orderData = {}) => {
  if (Array.isArray(orderData.salesReviewHistory)) {
    const approvalReview = [...orderData.salesReviewHistory]
      .reverse()
      .find((review) => review?.status === 'in_que' && (review?.reviewedBy || review?.reviewerName));

    if (approvalReview?.reviewedBy || approvalReview?.reviewerName) {
      return approvalReview.reviewedBy || approvalReview.reviewerName || '';
    }
  }

  return orderData.salesReviewedBy || '';
};

const DeliveredDetail = ({ db }) => {
  const { id } = useParams(); // This is a dispatch ID
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [firms, setFirms] = useState([]);
  const [activeTab, setActiveTab] = useState('shipments');
  const [expandedSections, setExpandedSections] = useState({}); // { dispatchId: { sectionKey: true/false } }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        const fetchedFirms = firmSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFirms(fetchedFirms);

        // 1. Get the current dispatch to find orderId
        const currentDocRef = doc(db, "dispatches", id);
        const currentDocSnap = await getDoc(currentDocRef);

        if (currentDocSnap.exists()) {
          const currentMData = currentDocSnap.data();
          const orderId = currentMData.orderId;

          if (orderId) {
            // 2. Fetch Order
            const orderSnap = await getDoc(doc(db, "orders", orderId));
            if (orderSnap.exists()) {
              const oData = { id: orderSnap.id, ...orderSnap.data() };
              setOrder(oData);

              // 3. Fetch Customer
              if (oData.customerID) {
                const custSnap = await getDoc(doc(db, "customers", oData.customerID));
                if (custSnap.exists()) {
                  setCustomer({ id: custSnap.id, ...custSnap.data() });
                }
              }

              // 4. Subscribe to ALL dispatches for this order
              const q = query(collection(db, "dispatches"), where("orderId", "==", orderId));
              const unsubscribe = onSnapshot(q, (snapshot) => {
                const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setDispatches(dispatchesList.sort((a, b) => (a.mocNo > b.mocNo ? 1 : -1)));
              });
              return () => unsubscribe();
            }
          } else {
            // Independent MOC (e.g. Interbranch Transfer)
            const virtualOrder = {
              id: 'virtual_' + id,
              orderNumber: currentMData.mocNo,
              firmId: currentMData.firmId || '',
              status: currentMData.dispatchStatus,
              typeOfMOC: currentMData.typeOfMOC,
              isVirtual: true
            };
            setOrder(virtualOrder);
            setDispatches([{ id: currentDocSnap.id, ...currentMData }]);

            if (currentMData.typeOfMOC === 'Interbranch Transfer' && currentMData.sourceWarehouseID) {
                const swSnap = await getDoc(doc(db, "warehouses", currentMData.sourceWarehouseID));
                if (swSnap.exists()) {
                    // We can reuse the 'customer' state to hold source warehouse for mapping logic
                    setCustomer({ id: swSnap.id, ...swSnap.data(), isWarehouse: true });
                }
            }
          }
        } else {
          alert("Dispatch not found");
          navigate('/ops/delivered');
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, navigate]);

  const toggleSection = (dispatchId, section) => {
    setExpandedSections(prev => ({
      ...prev,
      [dispatchId]: {
        ...(prev[dispatchId] || {}),
        [section]: !prev[dispatchId]?.[section]
      }
    }));
  };

  const processDCPreviewData = (mData, orderData, customerData) => {
    const salesShippingSnapshot = mData.typeOfMOC !== 'Interbranch Transfer'
      ? getSalesShippingSnapshot(mData, orderData)
      : null;
    const reconstructedComponents = (mData.components || []).map(mc => {
      let originalBaseQty = mc.perSet;
      if (!originalBaseQty && orderData) {
        orderData.boms?.forEach(bom => {
          const match = bom.components?.find(bc => bc.sku === mc.sku);
          if (match) originalBaseQty = match.baseQty;
        });
      }
      return { ...mc, perSet: originalBaseQty || 1, newQty: mc.newQty || 0, rentalQty: mc.rentalQty || 0 };
    });

    return {
      typeOfMOC: mData.typeOfMOC || '',
      firmId: mData.firmId || orderData?.firmId || '',
      challanNo: mData.challanNo || `DC-${mData.mocNo}`,
      dispatchDate: mData.dispatchDate || mData.deliveryDate || new Date().toISOString().split('T')[0],
      deliveryAgent: mData.deliveryAgent || '',
      deliveryMode: mData.deliveryMode || 'By Road',
      coordinator: mData.coordinator || mData.preparedBy || '',
      dispatchedThrough: mData.dispatchedThrough || '',
      vehicleType: mData.vehicleType || '',
      poNumber: mData.poNumber || '',
      poDate: mData.poDate || '',
      irnNo: mData.irnNo || '',
      billToCustomer: mData.billToCustomer || customerData?.company_name || mData.customerName || '',
      shipToCustomer: salesShippingSnapshot?.customerName || mData.shipToCustomer || mData.customerName || '',
      billToAddress: mData.billToAddress || formatAddress(orderData?.billingAddress),
      shipToAddress: getShippingSnapshotAddress(salesShippingSnapshot, mData.shipToAddress || mData.shippingAddress || ''),
      billToGstin: mData.billToGstin || customerData?.gstin || customerData?.gst_number || '',
      salesperson: mData.salesperson || orderData?.salesPerson || mData.approvedBySalesperson || '',
      orderBy: mData.orderBy || orderData?.billingAddress?.attention || '',
      orderByContact: mData.orderByContact || orderData?.billingAddress?.phone || '',
      sitePerson: salesShippingSnapshot?.contactName || mData.sitePerson || '',
      sitePersonContact: salesShippingSnapshot?.contactPhone || mData.sitePersonContact || '',
      billToEmail: mData.billToEmail || orderData?.billingAddress?.email || '',
      shipToEmail: salesShippingSnapshot?.contactEmail || mData.shipToEmail || '',
      itemCode: mData.itemCode || '',
      towerHeight: mData.towerHeight || '',
      totalMeter: mData.totalMeter || mData.totalMeterNumber || '',
      receivedByName: mData.receivedByName || '',
      receivedByContact: mData.receivedByContact || '',
      handoverDateTime: mData.handoverDateTime || '',
      taxInvoiceUrl: mData.taxInvoiceUrl || '',
      ewayBillUrl: mData.ewayBillUrl || '',
      einvoiceUrl: mData.einvoiceUrl || '',
      isDCConfirmed: mData.isDCConfirmed || false,
      approvedByFinance: mData.approvedByFinance || '',
      components: reconstructedComponents,
      vehicleProofUrl: mData.vehicleProofUrl || '',
      deliveryChallanUrl: mData.deliveryChallanUrl || '',
      bomSignedUrl: mData.bomSignedUrl || '',
    };
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    try {
      const decodedUrl = decodeURIComponent(url);
      const pathParts = decodedUrl.split('?')[0].split('/');
      return pathParts[pathParts.length - 1];
    } catch (e) {
      return 'File';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  if (loading || !order) return <Spinner />;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/delivered')} style={backBtnStyle}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0 }}>Order Tracking: {order.orderNumber}</h2>
        </div>
      </div>

      <div style={tabCardStyle}>
        <div style={tabContainerStyle}>
          <button
            style={activeTab === 'shipments' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('shipments')}
          >
            Shipment Tracking
          </button>
          {!order.isVirtual && (
            <button
              style={activeTab === 'order' ? activeTabStyle : tabStyle}
              onClick={() => setActiveTab('order')}
            >
              Order Preview
            </button>
          )}
        </div>

        <div style={tabContentStyle}>
          {activeTab === 'order' ? (
            customer ? <OrderPreview order={order} customer={customer} firms={firms} /> : <Spinner />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              {dispatches.map((dispatch, idx) => {
                const dcPreviewData = processDCPreviewData(dispatch, order, customer);
                const selectedFirm = firms.find(f => f.id === dcPreviewData.firmId);
                const isExpanded = (section) => expandedSections[dispatch.id]?.[section];
                const salesManagerApprovedAt = getSalesManagerApprovalTime(order);
                const salesManagerApprovedBy = getSalesManagerApprovalName(order);

                return (
                  <div key={dispatch.id} style={shipmentCardStyle}>
                    {/* CARD HEADER */}
                    <div style={shipmentHeaderStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={shipmentBadgeStyle}>{idx + 1}</div>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b' }}>MOC: {dispatch.mocNo}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>DC: {dispatch.challanNo || 'Pending'}</div>
                        </div>
                      </div>
                      {dispatch.dispatchStatus === 'delivered' ? (
                        <StatusBadge status="delivered" iconSize={12} />
                      ) : (
                        <div style={{ 
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <Truck size={14} />
                          {dispatch.dispatchStatus.toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* TIMELINE */}
                    <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
                      <HorizontalTimeline orderData={order} dispatchData={dispatch} title="Shipment Journey" />
                    </div>

                    {/* ACCORDION SECTIONS */}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      
                      {/* 1. DELIVERY SUMMARY */}
                      <div style={accordionStyle}>
                        <button onClick={() => toggleSection(dispatch.id, 'summary')} style={accordionBtnStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Truck size={18} color="#2563eb" /> 1. Delivery Summary</div>
                          {isExpanded('summary') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {isExpanded('summary') && (
                          <div style={accordionContentStyle}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                              <div><label style={labelStyle}>Date of Delivery</label><p style={valueStyle}>{dispatch.deliveryDetails?.deliveryDate ? formatDate(dispatch.deliveryDetails.deliveryDate) : '---'}</p></div>
                              <div><label style={labelStyle}>Handover Details</label><p style={valueStyle}>{dispatch.handoverDateTime || '---'}</p></div>
                              <div><label style={labelStyle}>Receiver Name</label><p style={valueStyle}>{dispatch.deliveryDetails?.receiverName || '---'}</p></div>
                              <div><label style={labelStyle}>Receiver Contact</label><p style={valueStyle}>{dispatch.deliveryDetails?.receiverContact || '---'}</p></div>
                              <div><label style={labelStyle}>Confirmed By</label><p style={valueStyle}>{dispatch.deliveryDetails?.deliveryConfirmedBy || '---'}</p></div>
                              <div><label style={labelStyle}>Vehicle No.</label><p style={valueStyle}>{dispatch.vehicleNumber || '---'}</p></div>
                              <div><label style={labelStyle}>Paid Transport Charges</label><p style={valueStyle}>{dispatch.deliveryDetails?.transportCharges ?? '---'}</p></div>
                              <div><label style={labelStyle}>Claimed Transport Charges(as per PO/order)</label><p style={valueStyle}>{order?.claimedChargesAsPerPoOrder ?? '---'}</p></div>
                            </div>
                            
                            {/* POD Proof */}
                            {dispatch.deliveryDetails?.podUrl && (
                              <div style={{ marginTop: '20px' }}>
                                <label style={labelStyle}>Signed POD (Proof of Delivery)</label>
                                <AttachmentCard
                                  href={dispatch.deliveryDetails.podUrl}
                                  label="Delivery Proof"
                                  name={getFileNameFromUrl(dispatch.deliveryDetails.podUrl)}
                                  icon={FileCheck}
                                  iconBackgroundColor="#dcfce7"
                                  iconColor="#16a34a"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 2. MOC DETAIL */}
                      <div style={accordionStyle}>
                        <button onClick={() => toggleSection(dispatch.id, 'moc')} style={accordionBtnStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Package size={18} color="#2563eb" /> 2. MOC Detail (BOM)</div>
                          {isExpanded('moc') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {isExpanded('moc') && (
                          <div style={accordionContentStyle}>
                            <MOCPreviewContent
                              mocData={{
                                ...dispatch,
                                salesManagerApprovedAt,
                                salesManagerApprovedBy
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* 3. DELIVERY CHALLAN */}
                      <div style={accordionStyle}>
                        <button onClick={() => toggleSection(dispatch.id, 'dc')} style={accordionBtnStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FileText size={18} color="#2563eb" /> 3. Delivery Challan</div>
                          {isExpanded('dc') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {isExpanded('dc') && (
                          <div style={accordionContentStyle}>
                            <DCPreviewContent dcData={dcPreviewData} selectedFirm={selectedFirm} />
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginTop: '20px' }}>
                              {dispatch.taxInvoiceUrl && (
                                <AttachmentCard
                                  href={dispatch.taxInvoiceUrl}
                                  label="Tax Invoice"
                                  name={getFileNameFromUrl(dispatch.taxInvoiceUrl)}
                                  iconBackgroundColor="#c7d2fe"
                                  iconColor="#4f46e5"
                                />
                              )}
                              {dispatch.ewayBillUrl && (
                                <AttachmentCard
                                  href={dispatch.ewayBillUrl}
                                  label="E-way Bill"
                                  name={getFileNameFromUrl(dispatch.ewayBillUrl)}
                                  iconBackgroundColor="#dcfce7"
                                  iconColor="#166534"
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 4. QC & LOGISTICS */}
                      <div style={accordionStyle}>
                        <button onClick={() => toggleSection(dispatch.id, 'qc')} style={accordionBtnStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ShieldCheck size={18} color="#2563eb" /> 4. QC & Driver Proofs</div>
                          {isExpanded('qc') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {isExpanded('qc') && (
                          <div style={accordionContentStyle}>
                            <QCPreview formData={dispatch} />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginTop: '20px' }}>
                              {dispatch.vehicleProofUrl && (
                                <AttachmentCard
                                  href={dispatch.vehicleProofUrl}
                                  label="Vehicle Proof"
                                  name={getFileNameFromUrl(dispatch.vehicleProofUrl)}
                                  icon={Truck}
                                  iconBackgroundColor="#e0f2fe"
                                  iconColor="#0284c7"
                                />
                              )}
                              {dispatch.bomSignedUrl && (
                                <AttachmentCard
                                  href={dispatch.bomSignedUrl}
                                  label="Signed BOM"
                                  name={getFileNameFromUrl(dispatch.bomSignedUrl)}
                                  iconBackgroundColor="#fee2e2"
                                  iconColor="#991b1b"
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- STYLES ---
const tabCardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minHeight: '600px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const valueStyle = { fontSize: '14px', color: '#1e293b', fontWeight: '500', marginBottom: '0' };
const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };

const tabContainerStyle = { display: 'flex', borderBottom: '2px solid #f3f4f6', marginBottom: '20px', gap: '10px' };
const tabStyle = { padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#64748b', transition: 'all 0.2s' };
const activeTabStyle = { ...tabStyle, color: '#2563eb', borderBottom: '2px solid #2563eb', marginBottom: '-2px' };
const tabContentStyle = { minHeight: '400px' };

const shipmentCardStyle = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
};

const shipmentHeaderStyle = {
  padding: '15px 20px',
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const shipmentBadgeStyle = {
  backgroundColor: '#2563eb',
  color: '#fff',
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 'bold'
};

const accordionStyle = {
  borderBottom: '1px solid #f1f5f9'
};

const accordionBtnStyle = {
  width: '100%',
  padding: '15px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '600',
  color: '#334155',
  transition: 'background 0.2s',
  textAlign: 'left'
};

const accordionContentStyle = {
  padding: '20px',
  backgroundColor: '#fdfdfd',
  borderTop: '1px solid #f8fafc'
};

export default DeliveredDetail;
